import { useQuery } from '@tanstack/react-query'

import type { UseQueryResult } from '@tanstack/react-query'
import type { IAnalyticsParams, ITopLevelAnalytic } from '~/types/analytic'

import { getAnalytics, getAnalyticsCacheState } from '~/api/application-analytics'
import { getTopLevelAppAnalytics } from '~/api/application-summary'

type SelectCallback<T> = (data: any) => T

const DEFAULT_CACHE_TIME = 0 // No cache time

////////// KEYS //////////

export const analyticsKeys = {
  adsets: () => [...analyticsKeys.all, 'adsets'] as const,
  all: ['analytics-graphs'] as const,
  cache: ['analytics-cache'] as const,
  graphData: (analyticsParams: IAnalyticsParams, appId?: string[]) =>
    [...analyticsKeys.all, 'graph-data', appId, analyticsParams] as const,
  topLevel: (analyticsParams: IAnalyticsParams, appIds: string[]) =>
    [...analyticsKeys.all, 'top-level', appIds, analyticsParams] as const,
  topLevelApp: (feature: string, appId?: string) => [...analyticsKeys.all, 'top-level-app', feature, appId] as const,
}

////////// QUERIES //////////

export const useAnalyticsDataQuery = <T = any>(
  analyticsParams: IAnalyticsParams,
  appIds: string[],
  options?: {
    cacheTime?: number
    doFetch?: boolean
    select?: SelectCallback<T>
    series?: any
  },
  queryFn?: (params: IAnalyticsParams) => Promise<any>
): UseQueryResult<T, unknown> => {
  return useQuery({
    enabled: !!appIds.length && options?.doFetch && !options?.series,
    gcTime: options?.cacheTime || DEFAULT_CACHE_TIME,
    queryFn: () => (queryFn ? queryFn(analyticsParams) : getAnalytics(appIds, analyticsParams)),
    queryKey: analyticsKeys.graphData(analyticsParams, appIds),
    select: options?.select,
  })
}

export const useTopLevelAppAnalyticsQuery = (
  feature: string,
  appId?: string,
  settings?: {
    cacheTime?: number
  },
  queryFn?: (feature: string) => Promise<any>
): UseQueryResult<ITopLevelAnalytic> => {
  return useQuery({
    enabled: !!appId,
    gcTime: settings?.cacheTime || DEFAULT_CACHE_TIME,
    queryFn: () => (queryFn ? queryFn(feature) : getTopLevelAppAnalytics(appId, feature)),
    queryKey: analyticsKeys.topLevelApp(feature, appId),
  })
}

export const useFetchAnalyticsCacheStateQuery = (): UseQueryResult<{ refreshed_at_ts: number }> => {
  return useQuery({ gcTime: 0, queryFn: getAnalyticsCacheState, queryKey: analyticsKeys.cache })
}
